import { gql, useMutation } from '@apollo/client'
import { PartialOrder } from 'account/models/PartialOrder'
import ContextErrorBanner from 'errors/components/ContextErrorBanner'
import ErrorBanner from 'errors/components/ErrorBanner'
import { useErrorContext } from 'errors/components/ErrorContextProvider'
import { IError } from 'errors/types/IError'
import { getErrorList } from 'errors/utils/getErrorList'
import { t } from 'localization'
import CreateAccountForm from 'login/components/CreateAccountForm'
import LoginForm from 'login/components/LoginForm'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import Image from 'shared/components/Image'
import Spinner from 'shared/components/Spinner'
import { colors, fonts, media, styles } from 'shared/lib'
import styled from 'styled-components'

const TP = 'manageOrder.manageOrderCreateAccount'

interface IManageOrderCreateAccountProps {
  order?: PartialOrder
  error?: string
  shouldShowLogin?: boolean
}

const Header = styled.section`
  padding: 40px ${styles.mobilePageLayoutPadding} 45px;
  background-color: ${colors.FC2_WHITE};
  text-align: center;
`
const StyledImage = styled(Image)`
  width: 200px;
`
const Title = styled.h2`
  ${fonts.HEADER_2}
`
const Description = styled.p`
  ${fonts.BODY_TEXT}
  max-width: 500px;
  margin: 0 auto;
`
const Subtitle = styled.h3`
  ${fonts.SUBTITLE_2};
  margin-top: 0;
`
const FormContainer = styled.div`
  margin: 0;
  background-color: ${colors.FC2_OFF_WHITE};
  ${media.small`
    padding-bottom: 150px;
  `}
`
const StyledErrorBanner = styled(ErrorBanner)`
  max-width: 587px;
  margin: 40px auto 0;
`
const StyledForm = styled.section`
  margin-top: 20px;
  padding: 30px;
  max-width: 587px;
  background-color: ${colors.FC2_WHITE};
  ${media.small`
    margin: 40px auto;
  `}
`
export const REQUEST_ORDER_TRANSFER_FROM_GUEST_TO_ACCOUNT = gql`
  mutation requestOrderTransferFromGuestToAccount($orderNumber: Int!) {
    requestOrderTransferFromGuestToAccount(orderNumber: $orderNumber) {
      id
    }
  }
`

const ManageOrderCreateAccount = ({
  error,
  order,
  shouldShowLogin = false,
}: IManageOrderCreateAccountProps) => {
  const router = useRouter()
  const { setErrors } = useErrorContext()
  const [isLogin, setIsLogin] = useState(shouldShowLogin)
  const [isLoading, setIsLoading] = useState(false)
  const [guestEmail, setGuestEmail] = useState('')
  const [requestOrderTransferFromGuestToAccount] = useMutation(
    REQUEST_ORDER_TRANSFER_FROM_GUEST_TO_ACCOUNT,
  )

  useEffect(() => {
    /**
     * this useEffect is necessary to trigger the rerender of the LoginForm so that
     * the disabled state of the email field is updated to true when a guest email is present
     *
     * when redirected from the guest checkout success page, a full page refresh is made
     * to log out the guest user which triggers server side render.
     *
     * initially when rendered on the server side, guestEmail is not defined and does
     * not set the email input to disabled. when the client side is updated with
     * `guestEmail` from sessionStorage, React needs to be made aware of this
     * change between server side render and client side render so the disabled state
     * can be updated. this useEffect with the setState forces a rerender on the clientside.
     */
    if (typeof window !== 'undefined') {
      if (window?.sessionStorage?.getItem('guest-email')) {
        setGuestEmail(window?.sessionStorage?.getItem('guest-email'))
      }
    }
  }, [])

  const handleCreate = () => {
    setIsLoading(true)
    setErrors([])

    const queryParams =
      order?.orderNumber && order?.status === 'goat_issue'
        ? `?active-order=${order.orderNumber}`
        : ''
    router.replace(`/my-account${queryParams}`, `/my-account${queryParams}`, {
      locale: router.locale,
    })
    setIsLoading(false)
  }

  const handleLogin = async () => {
    setIsLoading(true)
    setErrors([])

    try {
      const { data } = await requestOrderTransferFromGuestToAccount({
        variables: {
          orderNumber: order?.orderNumber,
        },
      })
      if (data) {
        const queryParams =
          order?.orderNumber && order?.status === 'goat_issue'
            ? `?active-order=${order.orderNumber}`
            : ''
        return router.replace(`/my-account${queryParams}`, `/my-account${queryParams}`, {
          locale: router.locale,
        })
      }
    } catch (error) {
      const errors = getErrorList(error.graphQLErrors)
      setErrors(errors)
    } finally {
      setIsLoading(false)
    }
  }

  const handleErrors = (errors: IError[]) => {
    setErrors(errors)
  }

  const productTemplate = order?.products?.[0]?.productTemplate
  const productImageIsPlaceholder = productTemplate?.pictureUrl?.includes('missing.png')

  return (
    <Spinner showSpinner={isLoading} threshold={0}>
      <Header data-testid="manage-order-header">
        {productTemplate?.pictureUrl && !productImageIsPlaceholder && (
          <StyledImage
            src={productTemplate.pictureUrl}
            alt={t(`${TP}.productTemplateImageAlt`, {
              defaultValue: '{name} Manage Order Hero',
              name: productTemplate.name || '',
            })}
            data-testid="manage-order-header-image"
            width={203}
            height={145}
          />
        )}
        <Title>{t(`${TP}.title`, 'Manage Your Order')}</Title>
        <Description>
          {t(
            `${TP}.description`,
            'To manage your order, please log in or create a Flight Club account using the email address associated with your recent order.',
          )}
        </Description>
      </Header>
      <FormContainer>
        {error && <StyledErrorBanner data-testid="manage-order-error" message={error} />}
        <StyledForm data-testid="manage-order-form">
          <Subtitle>
            {isLogin
              ? t(`${TP}.logIn`, 'Log in')
              : t(`${TP}.CompleteYourAccount`, 'Complete your account')}
          </Subtitle>
          <ContextErrorBanner />
          {isLogin && (
            <LoginForm
              data-testid="login-form"
              onCreateAccountClick={() => setIsLogin(false)}
              onLogin={handleLogin}
              onLoginError={handleErrors}
              initialEmail={guestEmail}
              isEmailDisabled={!!guestEmail}
              buttonText={t(`${TP}.loginToManage`, 'Log in to manage my order')}
              createAccountInsteadStyle="buttonAndDescription"
              createAccountInsteadTitle={t(
                `${TP}.dontHaveAccount`,
                "Don't have a Flight Club Account?",
              )}
              createAccountInsteadDescription={t(
                `${TP}.createAccount`,
                'Create your Flight Club account to manage your orders.',
              )}
            />
          )}
          {!isLogin && (
            <CreateAccountForm
              data-testid="create-account-form"
              guestCheckoutEmail={guestEmail}
              guestOrderNumber={order?.orderNumber || undefined}
              onCreateAccount={handleCreate}
              onCreateAccountError={handleErrors}
              buttonText={t(`${TP}.continue`, 'Continue')}
              location="view_order_link"
              loginInsteadStyle="buttonAndDescription"
              loginInsteadDescription={t(
                `${TP}.loginInGuest`,
                // tslint:disable-next-line:max-line-length
                'Log in to your Flight Club account to manage and link orders purchased as a guest.',
              )}
              loginInsteadCta={t(`${TP}.loginToManage`, 'Log in to manage my order')}
              onLoginClick={() => setIsLogin(true)}
              isGuestUpgrade
            />
          )}
        </StyledForm>
      </FormContainer>
    </Spinner>
  )
}

export default ManageOrderCreateAccount
